import { useState } from "react";
import { Globe, Linkedin, Pencil } from "lucide-react";
import { EditFieldDialog } from "@/components/EditFieldDialog";
import type { SublineItem } from "./RecordHeader";
import { websiteHost, websiteHref } from "./recordFormat";

/**
 * A website or LinkedIn link in a header subline, or "Add website" when unset.
 * Both open one small dialog. A failed save keeps it open with the error.
 */
export function LinkField({ label, value, href, display, onSave }: {
  label: string; value: string | null | undefined; href: string | null; display: string;
  onSave: (v: string | null) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {href ? (
        <span className="group inline-flex items-center gap-1">
          <a href={href} target="_blank" rel="noreferrer" className="hover:text-foreground hover:underline">{display}</a>
          <button type="button" aria-label={`Edit ${label}`} onClick={() => setOpen(true)} className="opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100">
            <Pencil className="h-3 w-3" />
          </button>
        </span>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="font-semibold text-primary-text hover:underline">Add {label}</button>
      )}
      <EditFieldDialog
        open={open}
        onOpenChange={setOpen}
        title={value ? `Edit ${label}` : `Add ${label}`}
        label={label.charAt(0).toUpperCase() + label.slice(1)}
        initialValue={value ?? ""}
        onSave={async (v) => { await onSave(v || null); }}
      />
    </>
  );
}

/** The website and LinkedIn items every company page puts in its header subline. */
export function companyLinkItems({ website, linkedinUrl, onSave }: {
  website: string | null | undefined;
  linkedinUrl: string | null | undefined;
  onSave: (field: "website" | "linkedinUrl", value: string | null) => Promise<unknown>;
}): SublineItem[] {
  return [
    { icon: <Globe />, content: <LinkField label="website" value={website ?? null} href={websiteHref(website)} display={websiteHost(website) ?? ""} onSave={(v) => onSave("website", v)} /> },
    { icon: <Linkedin />, content: <LinkField label="LinkedIn" value={linkedinUrl ?? null} href={websiteHref(linkedinUrl)} display="LinkedIn" onSave={(v) => onSave("linkedinUrl", v)} /> },
  ];
}
